import { useTranslation } from "react-i18next";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { User } from "../../../components/common/user";
import styles from "./styles.module.scss";
import {
  EContractType,
  ESenderRole,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import useLocalStorage from "../../../hooks/useLocalStorage/useLocalStorage";
import { IMyInfoData } from "../../../setup/api/services/profile/models";
import { IUser } from "../../../setup/api/services/auth/models";
import classNames from "classnames";
import { ContractContext } from "../../../providers/contract";

interface Props {
  userAction?: (type: string) => void;
  defaultParties?: (
    | (Partial<UserProfile> & { type?: ESenderRole })
    | undefined
  )[];
}

const ContractParties = ({ userAction, defaultParties }: Props) => {
  const { t } = useTranslation();
  const { contract, contractType } = useContext(ContractContext);
  const [myInfo] = useLocalStorage<IMyInfoData | null>("myInfo", null);
  const [userInfo] = useLocalStorage<IUser | null>("userInfo", null);
  const [parties, setParties] =
    useState<((Partial<UserProfile> & { type?: ESenderRole }) | undefined)[]>();

  const contractParties = useMemo(() => {
    const partiesArray: (
      | (Partial<UserProfile> & { type?: ESenderRole })
      | undefined
    )[] = [];

    if (contractType === EContractType.BROKERAGE_CONTRACT) {
      partiesArray.push({
        ...contract?.partiesProfiles?.BROKER?.[0],
        type: ESenderRole.BROKER,
      });
      partiesArray.push({
        ...contract?.partiesProfiles?.CUSTOMER?.[0],
        type: ESenderRole.CUSTOMER,
      });
      partiesArray.push({
        ...contract?.partiesProfiles?.PROVIDER?.[0],
        type: ESenderRole.PROVIDER,
      });
    } else if (
      contractType === EContractType.COMPLEX_CONTRACT &&
      defaultParties?.length
    ) {
      return partiesArray.concat(defaultParties);
    } else if (contractType === EContractType.QUICK_TRANSACTION) {
      partiesArray.push({
        ...contract?.provider,
        type: ESenderRole.PROVIDER,
      } as unknown as Partial<UserProfile> & { type: ESenderRole });
      contract?.customer &&
        partiesArray.push({
          ...contract?.customer,
          type: ESenderRole.CUSTOMER,
        } as unknown as Partial<UserProfile> & { type: ESenderRole });
    }

    return partiesArray;
  }, [contract, contractType, defaultParties]);

  useEffect(() => {
    setParties(contractParties);
  }, [contractParties]);

  const getUserType = useCallback(
    (user) => {
      if (contractType === EContractType.BROKERAGE_CONTRACT) {
        return user?.type === "BROKER"
          ? t("lbl_broker")
          : user?.type === "CUSTOMER"
          ? t("lbl_depositor")
          : t("lbl_receiver");
      } else if (contractType === EContractType.COMPLEX_CONTRACT) {
        return "";
      } else {
        return user?.type === "CUSTOMER" ? t("lbl_buyer") : t("lbl_seller");
      }
    },
    [contractType, t]
  );

  const renderUserDetails = useCallback(
    (user: Partial<UserProfile> & { type?: ESenderRole }) => {
      return (
        <div className={styles["owner"]}>
          <User
            imageUrl={user?.profile?.photoUrl}
            name={`${user?.profile?.fullName}${
              user?.id === userInfo?.id ? ` (${t("YOU")})` : ""
            }`}
            type={getUserType(user)}
            phoneNumber={user?.phoneNumber || ""}
            nationalIdVerified={user?.nationalIdVerified}
          />
        </div>
      );
    },
    [getUserType, t, userInfo?.id]
  );

  const renderUserActions = useCallback(
    (
      user:
        | (Partial<UserProfile> & {
            type?: ESenderRole;
          })
        | undefined,
      userAction?: (type: string) => void
    ) => {
      const brokers = contract?.partiesProfiles?.BROKER;
      let title = "";
      let phoneNumber = "";
      let type = "";

      if (user?.type === "CUSTOMER") {
        title = `${t("lbl_depositor")}`;
        phoneNumber = `${t("lbl_no_join")}`;
        type = "buyer";
      } else if (user?.type === "PROVIDER") {
        title = `${t("lbl_receiver")}`;
        phoneNumber = `${t("lbl_no_join")}`;
        type = "seller";
      }

      return (
        <div className={styles["no_owner"]}>
          <User
            name={title}
            phoneNumber={phoneNumber}
            userAction={
              brokers?.some((broker) => `${broker?.id}` == userInfo?.id)
                ? userAction?.bind(null, type)
                : undefined
            }
            userActionText={t("lbl_invite")}
          />
        </div>
      );
    },
    [contract, t, userInfo]
  );

  return (
    <div>
      <h3
        style={{ fontWeight: 600 }}
        className={classNames(
          {
            [styles["g900"]]: contractType === EContractType.BROKERAGE_CONTRACT,
          },
          {
            [styles["owner_header"]]:
              contractType === EContractType.BROKERAGE_CONTRACT,
          }
        )}>
        {t("lbl_transaction_parties")}
      </h3>
      <div
        className={
          contractType === EContractType.BROKERAGE_CONTRACT
            ? styles["contract_owners"]
            : styles["contract_owners_alt"]
        }>
        {parties?.map((party) => {
          return party?.id
            ? renderUserDetails(party)
            : renderUserActions(party, userAction);
        })}
      </div>
    </div>
  );
};

export default ContractParties;
